import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { styled } from "styled-components";
import * as SentryUtils from "../utils/sentry";
import { Button } from "../components/common/Button";
import {
  ErrorMessage,
  EyeIcon,
  FormContainer,
  FormGroup,
  FormHeading,
  FormText,
  Input,
  Label,
} from "../components/common/Form";
import { Confirmation } from "../components/common/Confirmation";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../stores/auth.store";

type Inputs = {
  password: string;
  confirmPassword: string;
};

const PasswordContainer = styled.div`
  position: relative;
`;

const EyeIconButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ChangePassword = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { changePassword } = useAuthStore();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Inputs>();

  const password = watch("password");

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    setIsLoading(true);
    try {
      await changePassword(data.password);
      setIsSubmitted(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      SentryUtils.captureException(error, {
        tags: { component: "ChangePassword", operation: "password_change" },
      });
      console.error("Password change failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmMessage =
    "LA TUA PASSWORD È STATA CAMBIATA CON SUCCESSO. VERRAI REINDIRIZZATO ALLA PAGINA DI LOGIN.";

  return isSubmitted ? (
    <Confirmation title="PASSWORD AGGIORNATA" message={confirmMessage} />
  ) : (
    <FormContainer>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormHeading>CAMBIA PASSWORD</FormHeading>
        <FormText>
          INSERISCI LA TUA NUOVA PASSWORD. LA PASSWORD DEVE CONTENERE ALMENO 6
          CARATTERI, UNA LETTERA MAIUSCOLA E UN CARATTERE SPECIALE.
        </FormText>

        <FormGroup>
          <Label htmlFor="password">Nuova Password</Label>
          <PasswordContainer>
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              $hasError={!!errors.password}
              {...register("password", {
                required: "Campo obbligatorio",
                minLength: {
                  value: 6,
                  message: "La password deve contenere almeno 6 caratteri",
                },
                pattern: {
                  value: /^(?=.*[A-Z])(?=.*[!@#$%^&*])/,
                  message:
                    "La password deve contenere almeno una lettera maiuscola e un carattere speciale",
                },
              })}
            />
            <EyeIconButton
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <EyeIcon isVisible={showPassword} />
            </EyeIconButton>
          </PasswordContainer>
          {errors.password && (
            <ErrorMessage role="alert">{errors.password.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="confirmPassword">Conferma Password</Label>
          <PasswordContainer>
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              $hasError={!!errors.confirmPassword}
              {...register("confirmPassword", {
                required: "Campo obbligatorio",
                validate: (value) =>
                  value === password || "Le password non coincidono",
              })}
            />
            <EyeIconButton
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={
                showConfirmPassword ? "Hide password" : "Show password"
              }
            >
              <EyeIcon isVisible={showConfirmPassword} />
            </EyeIconButton>
          </PasswordContainer>
          {errors.confirmPassword && (
            <ErrorMessage role="alert">
              {errors.confirmPassword.message}
            </ErrorMessage>
          )}
        </FormGroup>

        <Button
          type="submit"
          fullWidth
          loading={isLoading}
          loadingText="CAMBIO PASSWORD IN CORSO..."
        >
          CAMBIA PASSWORD
        </Button>
      </form>
    </FormContainer>
  );
};
